import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  api,
  CATEGORY_LABEL,
  formatDateTime,
  parseAttachments,
  type Report,
  type ReportCategory,
} from '../components/api'
import {
  Alert,
  Badge,
  Card,
  EmptyState,
  FormField,
  Input,
  Select,
} from '../components/ui'

export function ReportListPage() {
  const [items, setItems] = useState<Report[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [category, setCategory] = useState<'' | ReportCategory>('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api<{ items: Report[] }>('/api/reports', { query: { limit: 200 } })
      .then((d) => {
        if (!cancelled) setItems(d.items ?? [])
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return items.filter((r) => {
      if (category && r.category !== category) return false
      if (needle) {
        const hay = [r.body, r.customer_name ?? '', r.user_name ?? '']
          .join(' ')
          .toLowerCase()
        if (!hay.includes(needle)) return false
      }
      return true
    })
  }, [items, q, category])

  return (
    <>
      <div className="page-header">
        <h1>対応履歴</h1>
        <Link to="/reports/new" className="btn btn-primary btn-sm">
          ＋ 新規登録
        </Link>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <div className="form-row">
          <FormField label="キーワード検索" htmlFor="q">
            <Input
              id="q"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="本文・顧客名・起票者"
            />
          </FormField>
          <FormField label="カテゴリ" htmlFor="category">
            <Select
              id="category"
              value={category}
              onChange={(e) =>
                setCategory(e.target.value as '' | ReportCategory)
              }
            >
              <option value="">すべて</option>
              {(Object.keys(CATEGORY_LABEL) as ReportCategory[]).map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABEL[c]}
                </option>
              ))}
            </Select>
          </FormField>
        </div>
        <small className="muted">
          {loading ? '読み込み中…' : `${filtered.length} 件 / 全 ${items.length} 件`}
        </small>
      </Card>

      <Card>
        {loading ? (
          <p className="muted">読み込み中…</p>
        ) : filtered.length === 0 ? (
          <EmptyState title="該当する対応履歴がありません" />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 140 }}>発生日時</th>
                <th style={{ width: 110 }}>カテゴリ</th>
                <th>顧客</th>
                <th>内容</th>
                <th style={{ width: 90 }} className="text-right">
                  工数(分)
                </th>
                <th style={{ width: 100 }}>起票者</th>
                <th style={{ width: 60 }} className="text-right">
                  添付
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td>{formatDateTime(r.occurred_at)}</td>
                  <td>
                    <Badge variant="muted">{CATEGORY_LABEL[r.category]}</Badge>
                  </td>
                  <td>
                    <Link to={`/customers/${r.customer_id}`}>
                      {r.customer_name ?? '—'}
                    </Link>
                  </td>
                  <td>{truncate(r.body, 80)}</td>
                  <td className="text-right">{r.work_minutes}</td>
                  <td className="muted">{r.user_name ?? '—'}</td>
                  <td className="text-right">
                    {parseAttachments(r.attachments_r2_paths).length}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  )
}

function truncate(s: string, n: number) {
  if (!s) return ''
  return s.length > n ? `${s.slice(0, n)}…` : s
}
